/* eslint-disable react-hooks/exhaustive-deps */
import { PropsWithChildren, useEffect, useState } from "react";
import { useFormik } from "formik";
import classNames from "clsx";

import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { useApi } from "@/hooks/useApi";
import { userMap } from "@/utils/mappings/user";
import { transformData } from "@/utils/transformData";
import { useAttendanceId } from "@/hooks/useAttendanceId";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { replaceNullValuesWithEmptyString } from "@/utils/replaceNullValuesWithEmptyString";
import { preoperativeMedicalAdmission } from "@/utils/initialValues/surgicalCenter/medicalAdmission";

import { FormattedFormControl } from "@/components/FormControl/FormattedFormControl";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { ReactSelect } from "@/components/Selects/ReactSelect";
import { Divider } from "@/components/Divider";

export function MedicalAdmissionView() {
	const [skeleton, setSkeleton] = useState(true);
	const { attendanceId } = useAttendanceId();
	const { api } = useApi();
	const { width } = useWindowDimensions();

	const formik = useFormik({
		initialValues: preoperativeMedicalAdmission,
		onSubmit() {},
	});

	const values = formik.values;

	const fallbackUserInfo = {} as UserIdAndName;
	const user = values.user || fallbackUserInfo;
	const responsible = values.responsible || fallbackUserInfo;

	async function getAllData() {
		try {
			const { data: medicalAdmissionData } = await api.get(
				`/surgical-center/${attendanceId}/medical-admission`
			);

			const formattedMedicalAdmissionData =
				replaceNullValuesWithEmptyString(medicalAdmissionData);
			formik.setValues(formattedMedicalAdmissionData);

			const { user, responsible } = formattedMedicalAdmissionData;

			if (user) {
				formik.setFieldValue("user", transformData(user, userMap));
			}

			if (responsible) {
				formik.setFieldValue("responsible", transformData(responsible, userMap));
			}
		} finally {
			setSkeleton(false);
		}
	}

	function skeletonInlineWrapper({ children }: PropsWithChildren<unknown>) {
		return <span style={{ marginRight: "2%" }}>{children}</span>;
	}

	useEffect(() => {
		if (attendanceId) {
			getAllData();
		}
	}, [attendanceId]);

	return (
		<>
			<div className="px-5 pb-7">
				<TagHTMLOrSkeleton
					text="Atendimento"
					tag="h6"
					className="font-weight-bold mt-3 mb-6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row>
					<Col md={6}>
						{skeleton ? (
							<SkeletonWrapper count={2} />
						) : (
							<>
								<Form.Label htmlFor="doctor-responsible">Médico Resp.:</Form.Label>

								<ReactSelect
									isDisabled
									isSolid
									inputId="doctor-responsible"
									value={{ value: user.id, label: user.name }}
								/>
							</>
						)}
					</Col>

					<Col lg={4} md={6}>
						<FormattedFormControl
							id="attendance-date"
							type="datetime-local"
							label="Data do Atendimento:"
							labelClassName="fw-semibold"
							formattedFormControlClassName="form-control-solid"
							{...formik.getFieldProps("attendance_date")}
							name="attendance_date"
							skeleton={skeleton}
							readOnly
						/>
					</Col>
				</Row>
			</div>

			<Divider />

			<div className="px-5 pb-8">
				<TagHTMLOrSkeleton
					text="Anamnese do Paciente"
					tag="h6"
					className="font-weight-bold mt-3 mb-5"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row className="gap-3">
					<Col lg={8} md={12}>
						<FormattedFormControl
							as="textarea"
							id="history-of-current-disease"
							formattedFormControlClassName="form-control-solid"
							label="Histórico da Doença Atual"
							placeholder="Descreva"
							value={values.history_of_current_disease || ""}
							name="history_of_current_disease"
							skeleton={skeleton}
							skeletonHeight={45}
							readOnly
						/>
					</Col>

					<Col lg={8} md={12}>
						<FormattedFormControl
							as="textarea"
							id="personal-background"
							formattedFormControlClassName="form-control-solid"
							label="Antecedentes Pessoais"
							placeholder="Descreva"
							value={values.personal_background || ""}
							name="personal_background"
							skeleton={skeleton}
							skeletonHeight={45}
							readOnly
						/>
					</Col>

					<Col lg={8} md={12}>
						<FormattedFormControl
							as="textarea"
							id="physical-examination"
							formattedFormControlClassName="form-control-solid"
							label="Exame Físico"
							placeholder="Descreva"
							value={values.physical_examination || ""}
							name="physical_examination"
							skeleton={skeleton}
							skeletonHeight={45}
							readOnly
						/>
					</Col>

					<Col lg={8} md={12}>
						<FormattedFormControl
							as="textarea"
							id="provisional-diagnosis"
							formattedFormControlClassName="form-control-solid"
							label="Diagnóstico Provisório"
							placeholder="Descreva"
							value={values.provisional_diagnosis || ""}
							name="provisional_diagnosis"
							skeleton={skeleton}
							skeletonHeight={45}
							readOnly
						/>
					</Col>

					<Col lg={8} md={12}>
						<FormattedFormControl
							as="textarea"
							id="definitive-diagnosis"
							formattedFormControlClassName="form-control-solid"
							label="Diagnóstico Definitivo"
							placeholder="Descreva"
							value={values.definitive_diagnosis || ""}
							name="definitive_diagnosis"
							skeleton={skeleton}
							skeletonHeight={45}
							readOnly
						/>
					</Col>
				</Row>
			</div>

			<Divider />

			<div className="px-5 pb-10">
				<TagHTMLOrSkeleton
					text="Solicitação de Transferência entre Locais"
					tag="h6"
					className="font-weight-bold mt-3 mb-5"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row className="d-flex mb-3">
					<Col
						sm={6}
						className={classNames("d-flex align-items-center", width < 576 && "mb-3")}
					>
						{skeleton ? (
							<SkeletonWrapper
								width="45%"
								containerClassName="w-100"
								count={2}
								wrapper={skeletonInlineWrapper}
								inline
							/>
						) : (
							<>
								<Col lg="auto">
									<Form.Label className="mb-0 me-3" htmlFor="place-of-origin">
										Local de Origem:
									</Form.Label>
								</Col>

								<div className="flex-grow-1">
									<FormattedFormControl
										id="place-of-origin"
										placeholder="Descreva"
										formattedFormControlClassName="form-control-solid"
										{...formik.getFieldProps("place_of_origin")}
										name="place_of_origin"
										readOnly
									/>
								</div>
							</>
						)}
					</Col>

					<Col sm={6} className="d-flex align-items-center">
						{skeleton ? (
							<SkeletonWrapper
								width="45%"
								containerClassName="w-100"
								count={2}
								wrapper={skeletonInlineWrapper}
								inline
							/>
						) : (
							<>
								<Col lg="auto">
									<Form.Label className="mb-0 me-3" htmlFor="transfers-date">
										Data/Hora:
									</Form.Label>
								</Col>

								<div className="flex-grow-1">
									<FormattedFormControl
										type="datetime-local"
										id="transfers-date"
										formattedFormControlClassName="form-control-solid"
										{...formik.getFieldProps("transfers_date")}
										name="transfers_date"
										readOnly
									/>
								</div>
							</>
						)}
					</Col>
				</Row>

				<Row className="d-flex mb-3">
					<Col
						sm={6}
						className={classNames("d-flex align-items-center", width < 576 && "mb-3")}
					>
						{skeleton ? (
							<SkeletonWrapper
								width="45%"
								containerClassName="w-100"
								count={2}
								wrapper={skeletonInlineWrapper}
								inline
							/>
						) : (
							<>
								<Col lg="auto">
									<Form.Label
										className="mb-0 me-3"
										htmlFor="place-of-destination"
									>
										Local de Destino:
									</Form.Label>
								</Col>

								<div className="flex-grow-1">
									<FormattedFormControl
										id="place-of-destination"
										placeholder="Descreva"
										formattedFormControlClassName="form-control-solid"
										{...formik.getFieldProps("place_of_destination")}
										name="place_of_destination"
										readOnly
									/>
								</div>
							</>
						)}
					</Col>

					<Col sm={6} className="d-flex align-items-center">
						{skeleton ? (
							<SkeletonWrapper
								width="45%"
								containerClassName="w-100"
								count={2}
								wrapper={skeletonInlineWrapper}
								inline
							/>
						) : (
							<>
								<Col lg="auto">
									<Form.Label className="mb-0 me-3" htmlFor="responsible">
										Responsável:
									</Form.Label>
								</Col>

								<ReactSelect
									inputId="responsible"
									className="w-100"
									isSolid
									value={{ value: responsible.id, label: responsible.name }}
									isDisabled
								/>
							</>
						)}
					</Col>
				</Row>

				{skeleton ? (
					<Col sm={6}>
						<SkeletonWrapper
							width="45%"
							containerClassName="w-100"
							count={2}
							wrapper={skeletonInlineWrapper}
							inline
						/>
					</Col>
				) : (
					<div className="d-flex align-items-center">
						<Col lg="auto">
							<Form.Label className="mb-0 me-3" htmlFor="reason">
								Motivo:
							</Form.Label>
						</Col>

						<div className="flex-grow-1">
							<FormattedFormControl
								id="reason"
								placeholder="Descreva"
								formattedFormControlClassName="form-control-solid"
								{...formik.getFieldProps("reason")}
								name="reason"
								readOnly
							/>
						</div>
					</div>
				)}
			</div>
		</>
	);
}
